import { CaretSortIcon } from '@radix-ui/react-icons'
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import dayjs from 'dayjs'
import { memo, useState } from 'react'
import { shallow } from 'zustand/shallow'

import { useReportStore } from '@/modules/report/useReportsStore'
import { Button } from '@/modules/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/shared/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/modules/shared/components/ui/table'

type SalesCommission = {
  cliente: string
  descricao: string
  idContaCorrenteVendedor: number
  data: string
  valor: number
}

export const columns: ColumnDef<SalesCommission>[] = [
  {
    header: 'Cliente',
    accessorKey: 'cliente',
  },
  {
    header: 'Descrição',
    accessorKey: 'descricao',
  },
  {
    header: 'Código',
    accessorKey: 'idContaCorrenteVendedor',
  },
  {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Data
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    accessorKey: 'data',
    cell: ({ row }) => {
      const formatedDate = dayjs(row.original.data).utc().format('DD/MM/YYYY')

      return formatedDate
    },
  },
  {
    accessorKey: 'valor',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Valor
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return Number.parseFloat(row.original.valor.toFixed(2)).toLocaleString(
        'pt-BR',
        {
          style: 'currency',
          currency: 'BRL',
        },
      )
    },
  },
]

export default memo(function ListSalesCommissionReport() {
  const [sorting, setSorting] = useState<SortingState>([])
  const { loading, jsonSalesCommission } = useReportStore(
    (state) => ({
      loading: state.loading,
      jsonSalesCommission: state.jsonSalesCommission,
    }),
    shallow,
  )

  const table = useReactTable<SalesCommission>({
    data: jsonSalesCommission,
    columns,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
  })
  return (
    <Card className="mt-2">
      {!loading && jsonSalesCommission.length > 0 && (
        <>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Listagem de Comissões de Vendas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-4">
            <div className="flex flex-row justify-between">
              <div className="gap-2 flex ml-auto">
                <p>Total:</p>
                <span>
                  {Number.parseFloat(
                    jsonSalesCommission
                      .reduce((acc, curr) => acc + curr.valor, 0)
                      .toString(),
                  )
                    .toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })
                    .toString()}
                </span>
              </div>
            </div>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      Nenhum resultado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </>
      )}
    </Card>
  )
})
